import logger from '3-foundation/Logging/logger';
import { queryCDA } from '3-foundation/CDA/queryCDA';
import { mapSiteTheme } from '../PageContent/Mappers/PageSettings/siteThemeMapper';
import { mapFirstImage } from '3-foundation/PageContent/Mappers/DataTypes/imageMapper';
import { mapLinks } from '3-foundation/PageContent/Mappers/DataTypes/linkMapper';
import { buildTagTree } from './buildTagTree';

export const getSiteConfigFromCDA = async ({
	preview,
	host,
}: {
	preview: boolean;
	host: string;
}): Promise<{ id: Site.Config['id']; defaultLocale: Site.Config['defaultLocale']; cultures: Site.Config['cultures'] }> => {
	const profiler = logger.startTimer();
	const loggingName = 'getSiteConfig';

	try {
		if (!host) {
			logger.error('Internal Server Error \nDomain host could not be identified');
			throw new Error('Domain host could not be identified');
		}
		const { items: sites }: { items: Umbraco.TreeNode<Content.SiteIdentity>[] } = await queryCDA({
			siteId: null,
			url: 'umbraco/delivery/api/v2/content',
			urlParams: {
				filter: 'contentType:site',
				take: '100',
			},
			preview,
		});

		const currentSite = sites?.find((site) => site.properties.identity === host);

		if (!currentSite) {
			logger.error('Internal Server Error \nNo site found for host', { host });
			throw new Error(`No site configuration found for host: ${host}`);
		}
		const { defaultLocale } = currentSite.properties;

		const {
			items: homePages,
		}: { items: (Umbraco.TreeNode<{ siteIdentity: { properties: Content.SiteIdentity }[] }> & { cultures: Site.Config['cultures'] })[] } =
			await queryCDA({
				siteId: null,
				url: 'umbraco/delivery/api/v2/content',
				urlParams: {
					filter: 'contentType:homePage',
					fields: 'id,cultures,properties[siteIdentity]',
					expand: 'properties[siteIdentity]',
					take: '100',
				},
				locale: defaultLocale,
				preview,
				logging: loggingName,
			});
		if (homePages?.length < 1 || !homePages) {
			logger.error('Internal Server Error \nNo Home Pages identified');
			throw new Error('No Home Pages identified');
		}

		const multipleSites = homePages.length > 1;
		if (!multipleSites) {
			return { id: homePages[0]?.id, defaultLocale, cultures: homePages[0]?.cultures ?? {} };
		}

		const siteMatch = homePages?.find((homePage) => {
			const homePageIdentity = homePage?.properties?.siteIdentity?.[0]?.properties?.identity;
			return homePageIdentity?.toLowerCase() === host.toLowerCase();
		});

		if (siteMatch?.id) {
			return { id: siteMatch.id, defaultLocale, cultures: siteMatch.cultures ?? {} };
		}

		logger.error('Internal Server Error \nDomain host did not match any cms site identity', { siteMatch, host });
		throw new Error(`Domain host did not match any cms site identity for domain host: ${host}`);
	} catch (error) {
		throw new Error(`Website Identity could not be determined. \nCause: ${error.message}`, { cause: error });
	} finally {
		profiler.done({ message: loggingName, level: 'debug' });
	}
};

export const getSiteRedirectsFromCDA = async ({ siteId }: { siteId: string }): Promise<{ items: { properties }[] }> => {
	try {
		const urlParams = new URLSearchParams();
		urlParams.append('filter', 'contentType:customRedirect,contentRedirect');
		urlParams.append('take', '50000');

		return await queryCDA({
			url: 'umbraco/delivery/api/v2/content',
			urlParams,
			locale: null,
			siteId,
			logging: 'getSiteRedirects',
		});
	} catch (error) {
		throw new Error(`Could not get Redirects \nCause: ${error}`, { cause: error });
	}
};

export const getSiteTagsFromCDA = async ({ siteId, locale, preview }): Promise<Site.Tags> => {
	const profiler = logger.startTimer();
	const loggingName = 'getSiteTags';

	try {
		const { items: umbracoTags }: { items: Umbraco.TreeNode[] } = await queryCDA({
			url: 'umbraco/delivery/api/v2/content',
			urlParams: {
				filter: 'contentType:subjectTagsFolder,subjectTag,statusTagsFolder,statusTag,typeTagsFolder,typeTag',
				take: '1000',
			},
			siteId,
			locale,
			preview,
			logging: loggingName,
		});

		const tags = buildTagTree(umbracoTags);
		return tags;
	} catch (error) {
		throw new Error(`Website tags could not be determined. \nCause: ${error.message}`, { cause: error });
	} finally {
		profiler.done({ message: loggingName, level: 'debug', siteId, locale });
	}
};

export const getSiteFromCDA = async ({ siteId, locale, preview }): Promise<Omit<Site.Site, 'tags'>> => {
	const profiler = logger.startTimer();
	const loggingName = 'getSite';
	try {
		const data: {
			name: string;
			route: Umbraco.Link['route'];
			properties: {
				siteTitle: string;
				siteDesktopLogo: Umbraco.Image[] | null;
				siteMobileLogo: Umbraco.Image[] | null;
				serviceMenu: Umbraco.Link[] | null;
				pageSettings: { items: Content.Navigation[] };
				siteSettings: { items: { content: { properties; contentType } }[] };
				syncContacts: string;
				faviconAsSvg?: Umbraco.Image[] | null;
				homePageLinkLabel?: string;
				mindkeyCustomerId?: string | null;
				cookieConsent?: boolean;
			};
			cultures: Site.Site['locales'];
		} = await queryCDA({
			url: 'umbraco/delivery/api/v2/content/item',
			urlParams: {
				fields: 'properties[siteTitle,siteDesktopLogo,siteMobileLogo,siteSettings,pageSettings,serviceMenu,syncContacts,faviconAsSvg,homePageLinkLabel,mindkeyCustomerId,cookieConsent]',
				expand: 'properties[$all[properties[$all]]]',
			},
			siteId,
			locale,
			preview,
			logging: loggingName,
		});
		const { properties, cultures } = data ?? {};
		const {
			siteTitle,
			siteSettings,
			siteDesktopLogo,
			siteMobileLogo,
			serviceMenu,
			syncContacts,
			faviconAsSvg,
			homePageLinkLabel,
			mindkeyCustomerId,
			cookieConsent,
		} = properties ?? {};
		const arrayOfSiteSettings =
			siteSettings?.items?.map((setting) => {
				const { properties, contentType } = setting?.content ?? {};
				switch (contentType) {
					case 'matomo':
						return { matomo: properties };
					case 'mapsSettings':
						return { maps: properties };
					case 'languages':
						return { languages: properties };
					case 'searchSettings':
						return {
							searchSettings: {
								searchUrl: properties.searchUrl?.[0]?.route?.path || null,
							},
						};
					case 'themeSettings':
						return { theme: mapSiteTheme(properties) };
				}
			}) ?? [];
		const { navigationTitle, hideInNavigation } =
			data?.properties?.pageSettings?.items?.find((item) => item?.documentType === 'navigation')?.content?.properties ?? {};

		const contactsData: Site.ContactsData = JSON.parse(syncContacts);
		const site = {
			title: siteTitle,
			siteDesktopLogo: mapFirstImage(siteDesktopLogo),
			siteMobileLogo: mapFirstImage(siteMobileLogo),
			serviceMenu: mapLinks(serviceMenu),
			settings: { ...Object.assign({}, ...arrayOfSiteSettings) },
			root: {
				id: siteId,
				name: data?.name,
				url: data?.route?.path,
				title: navigationTitle ?? null,
				hidden: hideInNavigation ?? null,
			},
			locales: cultures,
			siteContacts: contactsData,
			favicons: {
				faviconAsSvg: faviconAsSvg?.[0]?.url || null,
			},
			homePageLinkLabel: homePageLinkLabel || null,
			mindkeyCustomerId: mindkeyCustomerId || null,
			cookieConsent: cookieConsent === true,
		};
		return site;
	} catch (error) {
		throw new Error('Could not get Site. \nCause: ' + error, { cause: error });
	} finally {
		profiler.done({ message: loggingName, level: 'debug' });
	}
};
